/* eslint-disable */
import { createStore } from "vuex";
import offlineSearchService from "@/service/offlineSearch.service";
import playlistStore from "@/store/playlist.store";
import sidemenuStore from "@/store/sidemenu.store";

const loadSong = (songId) => {
    return fetch("/api/song/" + songId, {
        method: "GET", 
        headers: {
            "content-type": "application/json"
        }
    });
};

const store = createStore({
    state: {
        open: false,
        closed: true,
        modal: null,
        loading: false,
        error: false,
        message: "",
        data: null
    },
    mutations: {
        OPEN (state, _data) {
            if (state.modal){
                state.modal.size('fullscreen');
                state.modal.open();
                state.open = true;
                state.closed = !state.open;
                state.data = _data;
            }
        },
        CLOSE (state) {
            if (state.modal){
                const shouldClose = state.open;
                state.closed = true;
                state.open = false;
                state.error = false;
                state.message = "";
                if (shouldClose){
                    state.modal.close();
                }
            }
        },
        DATA(state, _data) {
            if (state.modal){
                state.data = _data;
            } else {
                state.data = null;
            }
        },
        ERROR(state, response) {
            state.message = response.message;
            state.error = true;
        }
    },
    actions: {
        async open(context, songId) {
            context.commit("OPEN");
            context.state.loading = true;

            // 1. Try standard /api/song/:id fetch (handled by ServiceWorker StaleWhileRevalidate if cached)
            try {
                const response = await loadSong(songId);
                if (response.status === 200) {
                    const data = await response.json();
                    if (data) {
                        const raw = data.data || data;
                        context.commit("DATA", raw);
                        context.state.loading = false;
                        return;
                    }
                }
            } catch (e) {
                console.warn('[SongPreviewStore] Network fetch failed, checking offline fallback:', e);
            }

            // 2. Fallback: Search indexed offline songs
            try {
                if (offlineSearchService?.indexedItems?.songs) {
                    const localSong = offlineSearchService.indexedItems.songs.get(songId);
                    if (localSong) {
                        context.commit("DATA", localSong);
                        context.state.loading = false;
                        return;
                    }
                }
            } catch (err) {}

            // 3. Fallback: Search inside all cached playlists (in memory or cacheInstance)
            try {
                const searchInPlaylists = (playlists) => {
                    if (!Array.isArray(playlists)) return null;
                    for (const pl of playlists) {
                        const songs = pl.songs || [];
                        for (const s of songs) {
                            const sid = s.songId || s.song_id || s.song || s.data?.id || s.data?._id || s.id || s._id;
                            if (sid === songId || s._id === songId || s.id === songId) {
                                const songData = s.data || s;
                                return {
                                    _id: sid,
                                    id: sid,
                                    title: songData.title || s.title,
                                    artist: songData.artist || s.artist,
                                    lyric: s.modifiedLyric || songData.content || songData.lyric || s.content || s.lyric || '',
                                    content: s.modifiedLyric || songData.content || songData.lyric || s.content || s.lyric || '',
                                    modifiedLyric: s.modifiedLyric || songData.content || songData.lyric || s.content || s.lyric || '',
                                    tone: s.chord || songData.chord || songData.tone,
                                    tags: songData.tags || s.tags || []
                                };
                            }
                        }
                    }
                    return null;
                };

                const fromPlStore = searchInPlaylists(playlistStore?.state?.listPlaylists) ||
                                    searchInPlaylists(playlistStore?.state?.playlistsOwnedBy) ||
                                    searchInPlaylists(sidemenuStore?.state?.playlists) ||
                                    (playlistStore?.state?.playlist ? searchInPlaylists([playlistStore.state.playlist]) : null);

                if (fromPlStore) {
                    context.commit("DATA", fromPlStore);
                    context.state.loading = false;
                    return;
                }
            } catch (err) {}

            // 4. Fallback: Scan CacheStorage for any matching playlist-song or song URL
            if (typeof caches !== 'undefined') {
                try {
                    const cache = await caches.open('triad-content-cache');
                    const requests = await cache.keys();
                    for (const req of requests) {
                        if (req.url.includes(`/song/${songId}`) || req.url.includes(`/song/process/${songId}`)) {
                            const res = await cache.match(req);
                            if (res) {
                                const data = await res.clone().json();
                                const raw = data.data || data;
                                context.commit("DATA", raw);
                                context.state.loading = false;
                                return;
                            }
                        }
                    }
                } catch (err) {}
            }

            context.commit("ERROR", { message: "Song unavailable offline", status: 404 });
            context.state.loading = false;
        },
        close(context) {
            if (context.state.closed && !context.state.open) return;
            context.state.text = "";
            context.commit("CLOSE");
            context.commit("DATA", null);
        },
        error(context, message){
            context.commit("ERROR", message);
        }
    }
});

export default store;